import { z } from "zod/v4";

import {
  AgenticLogicPublishGateResultDtoDescriptions,
  AgenticLogicRulePublishJobDtoDescriptions,
} from "../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../descriptions/types";

// ---------------------------------------------------------------------------
// Publish-gate + publish-job vocabulary and DTOs.
//
// Evaluation budgets live in functions/agentic-logic/runtime/budget.
// ---------------------------------------------------------------------------

/**
 * Publish-gate keys, in canonical pipeline order. Wire format — append-only.
 *
 * CSP-mirrored order with the three recs-specific gates after
 * `runtime_timeout`: `chain_cost` (measured exhaustive wall time vs the
 * tightest assigned budget minus broker overhead; static refusal of unbounded
 * chains is a failure mode INSIDE this gate, not its own key), `scan_cost`
 * (paged-scan predicates priced against each scan's own declared bound and
 * budget — never against catalog size), and
 * `graph_consistency` (a documented no-op pass until the graph artifact
 * exists). The warning tier is exactly CSP's; `lambda_deploy` is the only
 * deploy-phase gate (dry runs skip it).
 */
export const AgenticLogicPublishGateKeyList = [
  "static_contract",
  "deps_locked",
  "org_secrets_resolved",
  "required_tests_passed",
  "bundle_size",
  "runtime_timeout",
  "chain_cost",
  "scan_cost",
  "graph_consistency",
  "ai_tests_generated",
  "ai_tests_passed",
  "user_tests_passed",
  "lambda_deploy",
] as const;

export const AgenticLogicPublishGateKeySchema = z.enum(
  AgenticLogicPublishGateKeyList
);

export type AgenticLogicPublishGateKey =
  (typeof AgenticLogicPublishGateKeyList)[number];

/** Canonical execution order for the publish pipeline (also the rail order). */
export const AGENTIC_LOGIC_PUBLISH_GATE_ORDER: readonly AgenticLogicPublishGateKey[] =
  AgenticLogicPublishGateKeyList;

/**
 * Gate-result statuses. Recs-owned so the domains stay uncoupled, but the
 * values are deliberately CSP's verbatim so the publish worker and rail port
 * cleanly. Wire format — append-only.
 */
export const AgenticLogicPublishGateStatusList = [
  "pending",
  "running",
  "passed",
  "failed",
  "warning",
  "blocked",
] as const;

export const AgenticLogicPublishGateStatusSchema = z.enum(
  AgenticLogicPublishGateStatusList
);

export type AgenticLogicPublishGateStatus =
  (typeof AgenticLogicPublishGateStatusList)[number];

/**
 * Why a gate failed, as a closed vocabulary. Wire format — append-only.
 *
 * Every failing gate's `detailsJson` carries one of these under `reason`, so
 * the two consumers that must act on a failure — the workspace's per-gate
 * `formatDetails` renderers and the repair loop's prompt — bind the same
 * discriminator instead of parsing the English `result` string. The remaining
 * fields of `detailsJson` are reason-specific and documented at each throw
 * site in `agentic-logic-rule-publish.gates.ts`.
 */
export const AgenticLogicGateFailureReasonList = [
  /** The entry module lacks the canonical `export async function evaluate(`. */
  "missing_evaluate_export",
  /** The determinism/escape screen refused a construct or import. */
  "source_guard_refusal",
  /** One or more tier-1 required checks failed (`failing[].key` names them). */
  "required_check_failed",
  /** The built bundle exceeds the size ceiling. */
  "bundle_over_limit",
  /** The rule's average invocation missed the assigned runtime budget. */
  "runtime_over_budget",
  /**
   * @deprecated No longer emitted. A data-driven chain used to be refused at
   * publish by a lexical scan of the rule source; the chain primitives now
   * enforce AGENTIC_LOGIC_MAX_CHAIN_ATTEMPTS_PER_INVOCATION at runtime and
   * `chain_cost` prices the ceiling instead. Retained because this list is
   * append-only wire format and historical publish jobs still carry it.
   */
  "refused_unbounded",
  /** The exhaustive chain run threw, so the chain could not be priced. */
  "measurement_failed",
  /** The exhaustive chain run finished but overran the allowed budget. */
  "chain_over_budget",
  /**
   * The worst-case per-invocation READ COUNT exceeds
   * AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION. `chain_cost` prices one
   * exhaustive measurement against both platform budgets — wall time and read
   * cardinality — because a fan-out written outside a chain lands entirely in
   * the fixed half of the latency model and is priced once, at the scenario's
   * own small cart. Read 33 fails the whole invocation; it does not truncate.
   */
  "reads_over_budget",
  /** The paged-scan plan cannot fit the tightest assigned budget. */
  "scan_refused",
  /** The rule graph disagrees with the code or violates the graph schema. */
  "graph_inconsistent",
  /** The generated suite failed shape validation before it ever ran. */
  "suite_rejected",
  /** A test suite ran and failed. */
  "suite_failed",
  /** A test suite disagreed with itself across two runs. */
  "nondeterministic_suite",
  /** Suite validation skipped because generation did not complete. */
  "suite_generation_incomplete",
  /** A gate threw something unstructured; fields carry the raw error. */
  "unexpected_error",
] as const;

export const AgenticLogicGateFailureReasonSchema = z.enum(
  AgenticLogicGateFailureReasonList
);

/**
 * One test's verdict from a suite gate run, keyed by the Vitest full name
 * (describe path and title joined with " > "). A suite gate's `detailsJson`
 * carries `outcomes: AgenticLogicSuiteTestOutcome[]` for the run it judged —
 * on a pass as well as a failure — so an editor shows exact per-test verdicts
 * instead of inferring them from `failures`. A gate written before this field
 * existed carries none; a reader falls back to `failures` then.
 */
export const AgenticLogicSuiteTestOutcomeSchema = z.object({
  testName: z.string(),
  state: z.enum(["passed", "failed", "skipped"]),
});

export type AgenticLogicSuiteTestOutcome = z.infer<
  typeof AgenticLogicSuiteTestOutcomeSchema
>;

export const AgenticLogicSuiteGateOutcomesSchema = z.array(
  AgenticLogicSuiteTestOutcomeSchema
);

/** One failed test a suite gate names, by Vitest full name. */
export const AgenticLogicSuiteTestFailureSchema = z.object({
  testName: z.string(),
  message: z.string(),
});

export type AgenticLogicSuiteTestFailure = z.infer<
  typeof AgenticLogicSuiteTestFailureSchema
>;

/**
 * The `detailsJson` a suite gate writes: the fields an editor reads to mark
 * each test. `failures` and `reason` are set on a failure; `outcomes` as
 * described above. Loose, because the gate also records its test count, logs
 * and any divergence.
 */
export const AgenticLogicSuiteGateDetailsSchema = z.looseObject({
  reason: AgenticLogicGateFailureReasonSchema.optional(),
  failures: z.array(AgenticLogicSuiteTestFailureSchema).optional(),
  outcomes: AgenticLogicSuiteGateOutcomesSchema.optional(),
});

export type AgenticLogicSuiteGateDetails = z.infer<
  typeof AgenticLogicSuiteGateDetailsSchema
>;

export type AgenticLogicGateFailureReason =
  (typeof AgenticLogicGateFailureReasonList)[number];

/**
 * WHERE a rule's evaluation budget came from. Wire format — append-only.
 *
 * Three materially different merchant-facing worlds, not an implementation
 * detail. The budget-bearing gates used to publish the threshold alone, so the
 * one consumer that must explain it had to guess — and it guessed "assigned",
 * which is false for two of the three:
 *
 *  - `assigned`    the rule has at least one enabled placement; the surfaces
 *                  the cap is taken over are those placements' surfaces.
 *  - `declared`    the rule is assigned nowhere; the surfaces are its own
 *                  `targetSurfaces`, and it is gated at the tightest cap it
 *                  could LATER be placed on (see
 *                  `agenticLogicTightestSurfaceBudgetMs`).
 *  - `any_surface` the rule is assigned nowhere AND declares nothing (builder);
 *                  the cap is the tightest on any surface at all.
 *
 * Carried on every budget-bearing gate's `detailsJson` beside `budgetMs` and
 * `budgetSurfaces`, and rendered by `renderAgenticLogicBudgetBasis`.
 */
export const AgenticLogicBudgetBasisList = [
  "assigned",
  "declared",
  "any_surface",
] as const;

export const AgenticLogicBudgetBasisSchema = z.enum(
  AgenticLogicBudgetBasisList
);

export type AgenticLogicBudgetBasis =
  (typeof AgenticLogicBudgetBasisList)[number];

/**
 * Publish-job statuses. Wire format — append-only. Identical to the
 * custom-shipping-provider vocabulary so the publish worker ports cleanly,
 * plus `cancelled`: a person stopped the run (`POST .../cancel-checks`). It is
 * terminal, and the worker is fenced from writing over it.
 */
export const AgenticLogicPublishJobStatusList = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
] as const;

export const AgenticLogicPublishJobStatusSchema = z.enum(
  AgenticLogicPublishJobStatusList
);

export type AgenticLogicPublishJobStatus =
  (typeof AgenticLogicPublishJobStatusList)[number];

/**
 * Publish-job mode. `publish` runs every gate and, on success, promotes the
 * draft to a new active version; `dry_run` runs the validation gates only and
 * never promotes. Wire format — append-only. Identical to the
 * custom-shipping-provider vocabulary.
 */
export const AgenticLogicPublishJobModeList = ["publish", "dry_run"] as const;

export const AgenticLogicPublishJobModeSchema = z.enum(
  AgenticLogicPublishJobModeList
);

export type AgenticLogicPublishJobMode =
  (typeof AgenticLogicPublishJobModeList)[number];

export const AgenticLogicPublishGateResultDtoSchema = z
  .object({
    gateKey: AgenticLogicPublishGateKeySchema.describe(
      AgenticLogicPublishGateResultDtoDescriptions.gateKey
    ),
    status: AgenticLogicPublishGateStatusSchema.describe(
      AgenticLogicPublishGateResultDtoDescriptions.status
    ),
    result: z
      .string()
      .nullish()
      .describe(AgenticLogicPublishGateResultDtoDescriptions.result),
    detailsJson: z
      .record(z.string(), z.unknown())
      .nullish()
      .describe(AgenticLogicPublishGateResultDtoDescriptions.detailsJson),
    startedAtTs: z
      .number()
      .nullish()
      .describe(AgenticLogicPublishGateResultDtoDescriptions.startedAtTs),
    finishedAtTs: z
      .number()
      .nullish()
      .describe(AgenticLogicPublishGateResultDtoDescriptions.finishedAtTs),
  })
  .describe(AgenticLogicPublishGateResultDtoDescriptions._schema);

export type AgenticLogicPublishGateResultDto = z.infer<
  typeof AgenticLogicPublishGateResultDtoSchema
>;

export const AgenticLogicRulePublishJobDtoSchema = z
  .object({
    id: z.string().describe(AgenticLogicRulePublishJobDtoDescriptions.id),
    ruleId: z
      .string()
      .describe(AgenticLogicRulePublishJobDtoDescriptions.ruleId),
    status: AgenticLogicPublishJobStatusSchema.describe(
      AgenticLogicRulePublishJobDtoDescriptions.status
    ),
    mode: AgenticLogicPublishJobModeSchema.describe(
      AgenticLogicRulePublishJobDtoDescriptions.mode
    ),
    startedAtTs: z
      .number()
      .nullable()
      .describe(AgenticLogicRulePublishJobDtoDescriptions.startedAtTs),
    finishedAtTs: z
      .number()
      .nullable()
      .describe(AgenticLogicRulePublishJobDtoDescriptions.finishedAtTs),
    resultingVersionId: z
      .string()
      .nullable()
      .describe(AgenticLogicRulePublishJobDtoDescriptions.resultingVersionId),
    gates: z
      .array(AgenticLogicPublishGateResultDtoSchema)
      .describe(AgenticLogicRulePublishJobDtoDescriptions.gates),
  })
  .describe(AgenticLogicRulePublishJobDtoDescriptions._schema);

export type AgenticLogicRulePublishJobDto = z.infer<
  typeof AgenticLogicRulePublishJobDtoSchema
>;

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicPublishGateResultDtoDescriptions,
  AgenticLogicPublishGateResultDto
>();
assertDescriptions<
  typeof AgenticLogicRulePublishJobDtoDescriptions,
  AgenticLogicRulePublishJobDto
>();
